/**
 * Zod schemas for the Playground WebSocket wire protocol.
 *
 * Every message — both client→server and server→client — shares the same
 * envelope: { type, roomId, seq, ts, payload }.
 *
 * `seq` is a monotonically increasing integer scoped to the room session.
 * The client uses gaps in `seq` to detect missed messages and request a
 * full-state resync.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Shared envelope fields
// ---------------------------------------------------------------------------
const BaseMessage = z.object({
    /** Room this message belongs to. */
    roomId: z.string().min(1),
    /** Monotonically increasing sequence number (per room, per connection). */
    seq: z.number().int().nonnegative(),
    /** Unix timestamp (ms) when the message was created. */
    ts: z.number().int().positive(),
});
// ---------------------------------------------------------------------------
// Client → Server messages
// ---------------------------------------------------------------------------
/** Player submits a game action. */
const ActionMessage = BaseMessage.extend({
    type: z.literal('action'),
    payload: z.object({
        /** Game-specific action object. Must be JSON-serialisable. */
        action: z.unknown(),
    }),
});
/** Client heartbeat. Server responds with `pong`. */
const PingMessage = BaseMessage.extend({
    type: z.literal('ping'),
    payload: z.object({}).optional(),
});
/** Player sends a chat message to the room. */
const ChatClientMessage = BaseMessage.extend({
    type: z.literal('chat'),
    payload: z.object({
        text: z.string().min(1).max(300),
    }),
});
/**
 * All messages the client may send.
 * Discriminated by the `type` field.
 */
export const ClientMessageSchema = z.discriminatedUnion('type', [
    ActionMessage,
    PingMessage,
    ChatClientMessage,
]);
// ---------------------------------------------------------------------------
// Server → Client messages
// ---------------------------------------------------------------------------
/** Full or incremental game state push. */
const StateMessage = BaseMessage.extend({
    type: z.literal('state'),
    payload: z.object({
        /** Game ID — included in the initial join state message so the client can identify the game. */
        gameId: z.string().optional(),
        /** Authoritative game state (game-defined shape). */
        gameState: z.unknown(),
        /** Status of the room. */
        status: z.enum(['waiting', 'playing', 'finished']),
        /** All currently connected players — included on initial join, omitted on in-game state updates. */
        players: z.array(z.object({
            id: z.string(),
            displayName: z.string(),
            isHost: z.boolean(),
            isConnected: z.boolean(),
        })).optional(),
    }),
});
/** A side-effect event (animation trigger, sound cue, etc.). */
const EventMessage = BaseMessage.extend({
    type: z.literal('event'),
    payload: z.object({
        /** Event type string defined by the game. */
        eventType: z.string(),
        /** Event-specific data. */
        data: z.unknown(),
    }),
});
/** Server-side error in response to a client action. */
const ErrorMessage = BaseMessage.extend({
    type: z.literal('error'),
    payload: z.object({
        code: z.string(),
        message: z.string(),
        /** Original seq that caused the error, if applicable. */
        originalSeq: z.number().int().optional(),
    }),
});
/** Server response to a `ping`. */
const PongMessage = BaseMessage.extend({
    type: z.literal('pong'),
    payload: z.object({
        /** Echo back the client's ping seq for RTT calculation. */
        pingSeq: z.number().int(),
    }),
});
/** A new player has joined the room. */
const PlayerJoinedMessage = BaseMessage.extend({
    type: z.literal('player_joined'),
    payload: z.object({
        player: z.object({
            id: z.string(),
            displayName: z.string(),
            isHost: z.boolean(),
            isConnected: z.boolean(),
        }),
    }),
});
/** A player has left or disconnected from the room. */
const PlayerLeftMessage = BaseMessage.extend({
    type: z.literal('player_left'),
    payload: z.object({
        playerId: z.string(),
        /** True if the player disconnected (may reconnect); false if they left intentionally. */
        disconnected: z.boolean(),
    }),
});
/** The game has ended. */
const GameOverMessage = BaseMessage.extend({
    type: z.literal('game_over'),
    payload: z.object({
        winners: z.array(z.string()),
        scores: z.record(z.string(), z.number()).optional(),
        reason: z.string(),
    }),
});
/** Host has started a rematch — non-host players should offer a one-tap rejoin. */
const RematchMessage = BaseMessage.extend({
    type: z.literal('rematch'),
    payload: z.object({
        newRoomId: z.string(),
    }),
});
/** Server notifies the player that their credit balance has changed. */
const CreditsUpdateMessage = BaseMessage.extend({
    type: z.literal('credits_update'),
    payload: z.object({
        balance: z.number(),
        earned: z.number().optional(),
    }),
});
/** Server broadcasts a chat message to all players in the room. */
const ChatBroadcastMessage = BaseMessage.extend({
    type: z.literal('chat_message'),
    payload: z.object({
        playerId: z.string(),
        displayName: z.string(),
        text: z.string(),
    }),
});
/**
 * All messages the server may send.
 * Discriminated by the `type` field.
 */
export const ServerMessageSchema = z.discriminatedUnion('type', [
    StateMessage,
    EventMessage,
    ErrorMessage,
    PongMessage,
    PlayerJoinedMessage,
    PlayerLeftMessage,
    GameOverMessage,
    RematchMessage,
    CreditsUpdateMessage,
    ChatBroadcastMessage,
]);
//# sourceMappingURL=message-schema.js.map